/* eslint-disable react-refresh/only-export-components, max-lines */
import { DimeButton, DimeSeparator, DimeToaster, toast } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/sonner')({
  component: SonnerDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function SonnerDocs() {
  return (
    <div>
      <DimeToaster richColors />
      <h1 className="mb-1 text-3xl font-bold">Toast (Sonner)</h1>
      <p className="mb-8 text-muted-foreground">An opinionated toast component for React.</p>

      <DocsSection title="Default">
        <Preview>
          <DimeButton
            variant="outline"
            onClick={() =>
              toast('Event has been created', {
                description: 'Sunday, December 03, 2023 at 9:00 AM',
                action: { label: 'Undo', onClick: () => {} },
              })
            }
          >
            Show Toast
          </DimeButton>
        </Preview>
      </DocsSection>

      <DocsSection title="Types">
        <Preview>
          <DimeButton variant="outline" onClick={() => toast('Event has been created')}>
            Default
          </DimeButton>
          <DimeButton variant="outline" onClick={() => toast.success('Event has been created')}>
            Success
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast.info('Be at the area 10 minutes before the event time')}
          >
            Info
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast.warning('Event start time cannot be earlier than 8am')}
          >
            Warning
          </DimeButton>
          <DimeButton variant="outline" onClick={() => toast.error('Event has not been created')}>
            Error
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() =>
              toast.promise<{ name: string }>(
                () => new Promise((resolve) => setTimeout(() => resolve({ name: 'Event' }), 2000)),
                {
                  loading: 'Loading...',
                  success: (data) => `${data.name} has been created`,
                  error: 'Error',
                },
              )
            }
          >
            Promise
          </DimeButton>
        </Preview>
      </DocsSection>

      <DocsSection title="Description">
        <Preview>
          <DimeButton
            variant="outline"
            onClick={() =>
              toast('Event has been created', {
                description: 'Monday, January 3rd at 6:00pm',
              })
            }
          >
            Show Toast
          </DimeButton>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono text-xs">description</code> to add a secondary line below
          the title.
        </p>
      </DocsSection>

      <DocsSection title="Position">
        <Preview>
          <DimeButton
            variant="outline"
            onClick={() => toast('Event has been created', { position: 'top-left' })}
          >
            Top Left
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast('Event has been created', { position: 'top-center' })}
          >
            Top Center
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast('Event has been created', { position: 'top-right' })}
          >
            Top Right
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast('Event has been created', { position: 'bottom-left' })}
          >
            Bottom Left
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast('Event has been created', { position: 'bottom-center' })}
          >
            Bottom Center
          </DimeButton>
          <DimeButton
            variant="outline"
            onClick={() => toast('Event has been created', { position: 'bottom-right' })}
          >
            Bottom Right
          </DimeButton>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono text-xs">position</code> per-toast to override the default
          position set on <code className="font-mono text-xs">DimeToaster</code>.
        </p>
      </DocsSection>

      <DocsSection title="DimeToaster props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="pb-2 pr-4 font-medium">Prop</th>
              <th className="pb-2 pr-4 font-medium">Type</th>
              <th className="pb-2 font-medium">Default</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">position</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">ToastPosition</td>
              <td className="py-2 text-muted-foreground font-mono text-xs">"top-center"</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">theme</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">
                "light" | "dark" | "system"
              </td>
              <td className="py-2 text-muted-foreground font-mono text-xs">"system"</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">richColors</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">boolean</td>
              <td className="py-2 text-muted-foreground font-mono text-xs">false</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">expand</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">boolean</td>
              <td className="py-2 text-muted-foreground font-mono text-xs">false</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">visibleToasts</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">number</td>
              <td className="py-2 text-muted-foreground font-mono text-xs">3</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">closeButton</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">boolean</td>
              <td className="py-2 text-muted-foreground font-mono text-xs">false</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-xs">toastOptions</td>
              <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">ToastOptions</td>
              <td className="py-2 text-muted-foreground font-mono text-xs">—</td>
            </tr>
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
